'use client'

import {useState} from 'react'

type Status = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Contact form wired to Netlify Forms (see public/__forms.html for the static
 * definition Netlify detects at build). Submits via fetch for a smooth UX, with
 * a native POST fallback (action/method) if JS is unavailable.
 */
export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle')

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    if (data.get('bot-field')) return // honeypot tripped — silently ignore

    setStatus('submitting')
    try {
      const body = new URLSearchParams()
      data.forEach((value, key) => body.append(key, typeof value === 'string' ? value : ''))
      body.set('form-name', 'contact')

      const res = await fetch('/__forms.html', {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
      })
      if (!res.ok) throw new Error(`Request failed: ${res.status}`)
      setStatus('success')
      form.reset()
    } catch {
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="form-note" role="status">
        <h3>Thank you — your message has been sent.</h3>
        <p>We’ll be in touch shortly. If your matter is urgent, please call our office.</p>
      </div>
    )
  }

  return (
    <form
      name="contact"
      method="POST"
      action="/__forms.html"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={onSubmit}
      className="contact-form"
    >
      <input type="hidden" name="form-name" value="contact" />
      <p className="hp" aria-hidden="true">
        <label>
          Don’t fill this out if you’re human: <input name="bot-field" tabIndex={-1} autoComplete="off" />
        </label>
      </p>

      <div className="field">
        <label htmlFor="cf-name">Name</label>
        <input id="cf-name" name="name" type="text" required autoComplete="name" />
      </div>
      <div className="field">
        <label htmlFor="cf-email">Email</label>
        <input id="cf-email" name="email" type="email" required autoComplete="email" />
      </div>
      <div className="field">
        <label htmlFor="cf-phone">
          Phone <span className="opt">(optional)</span>
        </label>
        <input id="cf-phone" name="phone" type="tel" autoComplete="tel" />
      </div>

      <div className="field consent-field">
        <p className="consent-text">
          By providing my phone number, I consent to receive SMS text messages from Dolman Oral
          Surgery for appointment reminders, marketing messages, and general two-way communication.
          Msg frequency varies. Msg&amp;data rates may apply. Reply HELP for support. Reply STOP to
          opt out. *
        </p>
        <label className="consent">
          <input type="checkbox" name="smsConsent" value="I consent" required />
          <span>I consent</span>
        </label>
      </div>

      <fieldset className="field choice-field">
        <legend>Are you a new patient?</legend>
        <div className="choices">
          <label className="choice">
            <input type="radio" name="newPatient" value="Yes" required /> <span>Yes</span>
          </label>
          <label className="choice">
            <input type="radio" name="newPatient" value="No" /> <span>No</span>
          </label>
        </div>
      </fieldset>

      <fieldset className="field choice-field">
        <legend>How can we help you?</legend>
        <div className="choices">
          <label className="choice">
            <input type="radio" name="helpType" value="Request an appointment" required />{' '}
            <span>Request an appointment</span>
          </label>
          <label className="choice">
            <input type="radio" name="helpType" value="I need more information" />{' '}
            <span>I need more information</span>
          </label>
        </div>
      </fieldset>

      <div className="field">
        <label htmlFor="cf-message">Message</label>
        <textarea id="cf-message" name="message" rows={6} required />
      </div>

      {status === 'error' && (
        <p className="form-error" role="alert">
          Sorry — something went wrong sending your message. Please try again, or call our office directly.
        </p>
      )}

      <button type="submit" className="btn solid" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Send Message'}
      </button>
    </form>
  )
}
